import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, DollarSign, Ticket, Send, Building2 } from 'lucide-react';
import api from '../services/api';

const ticketStatusBadge = { abierto: 'badge-info', en_curso: 'badge-warning', esperando_cliente: 'badge-gray', resuelto: 'badge-success', cerrado: 'badge-gray' };
const ticketStatusLabels = { abierto: 'Abierto', en_curso: 'En curso', esperando_cliente: 'Esperando cliente', resuelto: 'Resuelto', cerrado: 'Cerrado' };

const collectionBadge = (inv) => {
  if (inv.cancelledAt) return 'badge-gray';
  if (inv.collectionStatus === 'cobrada') return 'badge-success';
  if (inv.collectionStatus === 'parcial') return 'badge-warning';
  if (new Date(inv.dueDate) < new Date(new Date().toDateString())) return 'badge-danger';
  return 'badge-warning';
};

const collectionLabel = (inv) => {
  if (inv.cancelledAt) return 'Anulada';
  if (inv.collectionStatus === 'cobrada') return 'Cobrada';
  if (inv.collectionStatus === 'parcial') return 'Parcial';
  if (new Date(inv.dueDate) < new Date(new Date().toDateString())) return 'Vencida';
  return 'Pendiente';
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await api.get('/dashboard/stats');
        setStats(res.data);
      } catch (error) {
        console.error('Error cargando estadísticas:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando dashboard...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Resumen de tu actividad</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue"><Users size={24} /></div>
          <div className="stat-info">
            <h3>{stats?.totalCustomers || 0}</h3>
            <p>Clientes totales</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon yellow"><Ticket size={24} /></div>
          <div className="stat-info">
            <h3>{stats?.openTickets || 0}</h3>
            <p>Tickets abiertos</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><DollarSign size={24} /></div>
          <div className="stat-info">
            <h3>{formatCurrency(stats?.totalRevenue)}</h3>
            <p>Ingresos cobrados</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon red"><FileText size={24} /></div>
          <div className="stat-info">
            <h3>{formatCurrency(stats?.pendingRevenue)}</h3>
            <p>Pendiente de cobro</p>
          </div>
        </div>
      </div>

      <div className="card dashboard-pending-card">
        <div className="card-header">
          <h2 className="card-title">Avisos de cobro pendiente/parcial</h2>
          <div className="dashboard-pending-total">
            Total pendiente: {formatCurrency(stats?.pendingCollectionTotal)}
          </div>
        </div>
        {stats?.pendingCollections?.length > 0 ? (
          <div className="dashboard-list">
            {stats.pendingCollections.map((invoice) => {
              const total = Number(invoice.total || 0);
              const collected = Number(invoice.collectedAmount || 0);
              const remaining = Math.max(0, total - collected);
              const isPartial = collected > 0;
              const badgeStyle = isPartial
                ? { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }
                : { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' };
              const rowBorderColor = isPartial ? '#f59e0b' : '#dc2626';
              return (
                <div
                  key={invoice.id}
                  className={`dashboard-row dashboard-row--clickable ${isPartial ? 'dashboard-row--partial' : 'dashboard-row--pending'}`}
                  onClick={() => navigate(`/invoices/edit/${invoice.id}`)}
                  style={{ borderLeft: `4px solid ${rowBorderColor}` }}
                >
                  <div className="dashboard-row-main">
                    <div className="dashboard-row-title">{invoice.invoiceNumber} · {invoice.customer?.name}</div>
                    <div className="dashboard-row-sub">Vence: {formatDate(invoice.dueDate)}</div>
                  </div>
                  <div className="dashboard-row-aside">
                    <span
                      className={`badge ${isPartial ? 'badge-warning' : 'badge-danger'}`}
                      style={{
                        ...badgeStyle,
                        padding: '4px 10px',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                    >
                      {isPartial ? 'Parcial' : 'Pendiente'}
                    </span>
                    <div className="dashboard-row-amount">{formatCurrency(remaining)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state"><p>No hay facturas pendientes de cobro</p></div>
        )}
      </div>

      <div className="dashboard-two-col">
        {/* Recent Tickets */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Últimos tickets</h2>
          </div>
          {stats?.recentTickets?.length > 0 ? (
            <div className="dashboard-list">
              {stats.recentTickets.map(ticket => (
                <div
                  key={ticket.id}
                  className="dashboard-row dashboard-row--clickable"
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                >
                  <div className="dashboard-row-main">
                    <div className="dashboard-row-title">{ticket.code} — {ticket.subject}</div>
                    <div className="dashboard-row-sub">{ticket.customer?.name}</div>
                  </div>
                  <span className={`badge ${ticketStatusBadge[ticket.status] || 'badge-gray'}`}>{ticketStatusLabels[ticket.status] || ticket.status}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state"><p>No hay tickets aún</p></div>
          )}
        </div>

        {/* Recent Invoices */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Últimas facturas</h2>
          </div>
          {stats?.recentInvoices?.length > 0 ? (
            <div className="dashboard-list">
              {stats.recentInvoices.map(inv => (
                <div
                  key={inv.id}
                  className="dashboard-invoice-row dashboard-row--clickable"
                  onClick={() => navigate(`/invoices/edit/${inv.id}`)}
                >
                  <div className="dashboard-invoice-row__top">
                    <div className="dashboard-invoice-row__title">{inv.invoiceNumber}</div>
                    <div className="dashboard-invoice-row__amount">{formatCurrency(parseFloat(inv.total))}</div>
                  </div>
                  <div className="dashboard-invoice-row__bottom">
                    <div className="dashboard-invoice-row__sub">{inv.customer?.name} · {formatDate(inv.date)}</div>
                    <div className="dashboard-invoice-row__marks">
                      <span
                        className="dashboard-invoice-row__icon"
                        style={{ color: inv.sentToCustomerAt ? '#15803d' : '#cbd5e1' }}
                        title={inv.sentToCustomerAt ? `Enviada al cliente el ${formatDate(inv.sentToCustomerAt)}` : 'No enviada al cliente'}
                      >
                        <Send size={14} />
                      </span>
                      <span
                        className="dashboard-invoice-row__icon"
                        style={{ color: inv.sentToAccountantAt ? '#15803d' : '#cbd5e1' }}
                        title={inv.sentToAccountantAt ? `Enviada a asesoría el ${formatDate(inv.sentToAccountantAt)}` : 'No enviada a asesoría'}
                      >
                        <Building2 size={14} />
                      </span>
                      <span className={`badge ${collectionBadge(inv)}`}>{collectionLabel(inv)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state"><p>No hay facturas aún</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
